import React, { useState } from "react";
import { api } from "./api";
import { Lead } from "./types";

function bandClass(band: string) {
  if (band === "Human-crafted" || band === "Strong") return "bg-ready";
  if (band === "Mixed")                              return "bg-needs";
  return "bg-not";
}

function fmt(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

export default function AdminPage() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await api.getLeads(password);
      setLeads(data);
      setAuthed(true);
    } catch (err: any) {
      setError(err.message || "Invalid password");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string) {
    setDeleting(id);
    try {
      await api.deleteLead(password, id);
      setLeads(prev => prev.filter(l => l.id !== id));
    } catch (err: any) {
      alert("Delete failed: " + (err.message || "Unknown error"));
    } finally {
      setDeleting(null);
    }
  }

  if (!authed) {
    return (
      <div className="password-gate">
        <form className="password-form" onSubmit={handleLogin}>
          <h2>Admin Access</h2>
          <p>Core6 Slop Detector — Leads Dashboard</p>
          <input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          {error && <div className="error-box" style={{ marginBottom: 12 }}>{error}</div>}
          <button className="btn-primary" type="submit" disabled={loading}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  const withEmail = leads.filter((l) => l.email).length;
  const avgScore = leads.length
    ? Math.round(leads.reduce((s, l) => s + l.overallScore, 0) / leads.length)
    : 0;

  return (
    <div className="admin-wrap">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: "0 0 4px" }}>Leads Dashboard</h1>
          <span style={{ color: "#9A9088", fontSize: 14, fontFamily: "Space Mono, monospace", textTransform: "uppercase", letterSpacing: 1 }}>Core6 Slop Detector</span>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <StatChip label="Total checks" value={leads.length} />
          <StatChip label="With email" value={withEmail} />
          <StatChip label="Avg score" value={avgScore} />
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "#9A9088" }}>
          No checks yet — share the slop detector to start collecting.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Page URL</th>
                <th>Email</th>
                <th>Score</th>
                <th>Band</th>
                <th>Date</th>
                <th>Contacted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} style={{ opacity: deleting === lead.id ? 0.4 : 1, transition: "opacity .2s" }}>
                  <td style={{ fontWeight: 600, color: "#1A1714", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <a href={lead.url} target="_blank" rel="noopener noreferrer" style={{ color: "#1A1714", textDecoration: "none" }} title={lead.url}>
                      {lead.url?.replace(/^https?:\/\//, "")}
                    </a>
                  </td>
                  <td style={{ color: lead.email ? "#1A1714" : "#9A9088" }}>
                    {lead.email ? (
                      <a href={`mailto:${lead.email}`} style={{ color: "#B31C1C", textDecoration: "none" }}>
                        {lead.email}
                      </a>
                    ) : "—"}
                  </td>
                  <td>
                    <strong style={{ color: lead.overallScore >= 65 ? "#1B6B3A" : lead.overallScore >= 40 ? "#C07820" : "#B31C1C" }}>
                      {lead.overallScore}
                    </strong>
                  </td>
                  <td>
                    <span className={`band-pill ${bandClass(lead.band)}`}>{lead.band}</span>
                  </td>
                  <td style={{ color: "#9A9088", whiteSpace: "nowrap" }}>{fmt(lead.submittedAt)}</td>
                  <td>
                    <span style={{ color: lead.contacted ? "#1B6B3A" : "#9A9088", fontWeight: 600 }}>
                      {lead.contacted ? "Yes" : "No"}
                    </span>
                  </td>
                  <td style={{ textAlign: "center", width: 40 }}>
                    <button
                      onClick={() => handleDelete(lead.id)}
                      disabled={deleting === lead.id}
                      title="Delete"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: deleting === lead.id ? "not-allowed" : "pointer",
                        padding: "4px 6px",
                        borderRadius: 4,
                        color: "#9A9088",
                        fontSize: 16,
                        lineHeight: 1,
                        transition: "color .15s",
                      }}
                      onMouseEnter={e => (e.currentTarget.style.color = "#B31C1C")}
                      onMouseLeave={e => (e.currentTarget.style.color = "#9A9088")}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ background: "#FAF8F5", border: "1px solid #E8E1D8", borderRadius: 8, padding: "10px 18px", textAlign: "center", minWidth: 90 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#1A1714", fontFamily: "Outfit, sans-serif" }}>{value}</div>
      <div style={{ fontSize: 11, color: "#9A9088", marginTop: 2, fontFamily: "Space Mono, monospace", textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
    </div>
  );
}
